import {
  Button,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@catering/ui';
import { AlertTriangle, Pencil, Plus, Send, Trash2, Undo2 } from 'lucide-react';
import type { JSX } from 'react';

import type { DietaryCoverageEntry, MenuCalendarEntry } from '../../../calendar/api';
import { mapDietaryWarning } from '../../../calendar/calendar-api-errors';
import { formatCalendarDate } from '../../../calendar/format-calendar-date';

export interface CalendarGridServiceType {
  id: string;
  name: string;
  displayOrder: number;
}

export interface CalendarGridPosition {
  id: string;
  name: string;
  serviceTypeId: string;
  displayOrder: number;
}

export interface CalendarGridMenu {
  id: string;
  name: string;
}

export interface CalendarGridProps {
  /** ISO calendar days, ordered ascending. */
  dates: string[];
  serviceTypes: CalendarGridServiceType[];
  /** Active positions to render as columns, grouped under their service type and ordered by `displayOrder` within each group. */
  positions: CalendarGridPosition[];
  entries: MenuCalendarEntry[];
  menusById: Record<string, CalendarGridMenu>;
  /** Mirror of `menu.calendar.manage` — UX only, never a security boundary; the API enforces the real check. */
  canManage: boolean;
  /** From `GET /calendar/dietary-summary`, keyed by ISO date. A date absent from this map (e.g. an empty range result) renders with no warning, never throws. */
  dietaryCoverageByDate?: Record<string, DietaryCoverageEntry[]>;
  onEdit?: (entry: MenuCalendarEntry) => void;
  onTogglePublish?: (entry: MenuCalendarEntry) => void;
  onDelete?: (entry: MenuCalendarEntry) => void;
  /** Empty-cell "asignar" action: opens the create form restricted to that position's service type, with `positionId` and `serviceDate` pre-filled. */
  onAssign?: (date: string, position: CalendarGridPosition) => void;
}

/**
 * `menu-calendar/spec.md` → "Pantalla de calendario en back-office": rows =
 * service dates, columns grouped by service type and, within each group, by
 * position ordered by `displayOrder`.
 */
export function CalendarGrid({
  dates,
  serviceTypes,
  positions,
  entries,
  menusById,
  canManage,
  dietaryCoverageByDate,
  onEdit,
  onTogglePublish,
  onDelete,
  onAssign,
}: CalendarGridProps): JSX.Element {
  const sortedServiceTypes = [...serviceTypes].sort((a, b) => a.displayOrder - b.displayOrder);
  const positionsByServiceType = new Map<string, CalendarGridPosition[]>();

  for (const position of [...positions].sort((a, b) => a.displayOrder - b.displayOrder)) {
    const group = positionsByServiceType.get(position.serviceTypeId) ?? [];
    group.push(position);
    positionsByServiceType.set(position.serviceTypeId, group);
  }

  const serviceTypeGroups = sortedServiceTypes
    .map((serviceType) => ({
      serviceType,
      positions: positionsByServiceType.get(serviceType.id) ?? [],
    }))
    .filter((group) => group.positions.length > 0);

  function entryFor(date: string, positionId: string): MenuCalendarEntry | undefined {
    return entries.find((entry) => entry.serviceDate === date && entry.positionId === positionId);
  }

  return (
    <Table>
      <caption className="sr-only">Calendario de menús</caption>
      <thead>
        <tr>
          <TableHeader rowSpan={2} className="min-w-[120px]">
            Fecha
          </TableHeader>
          {serviceTypeGroups.map((group) => (
            <TableHeader
              key={group.serviceType.id}
              colSpan={group.positions.length}
              className="text-center"
            >
              {group.serviceType.name}
            </TableHeader>
          ))}
        </tr>
        <tr>
          {serviceTypeGroups.flatMap((group) =>
            group.positions.map((position) => (
              <TableHeader key={position.id} className="min-w-[220px]">
                {position.name}
              </TableHeader>
            )),
          )}
        </tr>
      </thead>
      <TableBody>
        {dates.map((date) => {
          const hasEntries = entries.some((entry) => entry.serviceDate === date);

          return (
            <TableRow key={date}>
              <TableCell className="font-medium text-slate-900">
                <div className="flex items-center gap-1.5">
                  {formatCalendarDate(date)}
                  {hasEntries ? renderDietaryWarningIcon(dietaryCoverageByDate?.[date]) : null}
                </div>
              </TableCell>
              {serviceTypeGroups.flatMap((group) =>
                group.positions.map((position) => {
                  const entry = entryFor(date, position.id);

                  return (
                    <TableCell
                      key={position.id}
                      className="align-top min-w-[220px]"
                      data-testid={`calendar-cell-${date}-${position.id}`}
                    >
                      {entry === undefined ? (
                        canManage ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Plus}
                            onClick={() => onAssign?.(date, position)}
                          >
                            Asignar
                          </Button>
                        ) : null
                      ) : (
                        <CalendarCellEntry
                          entry={entry}
                          menuName={menusById[entry.menuId]?.name ?? entry.menuId}
                          canManage={canManage}
                          onEdit={onEdit}
                          onTogglePublish={onTogglePublish}
                          onDelete={onDelete}
                        />
                      )}
                    </TableCell>
                  );
                }),
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** `menu-calendar/spec.md` → "Ícono de advertencia visible en fecha sin opción sin TACC": informative only, never blocks anything. */
function renderDietaryWarningIcon(
  coverage: DietaryCoverageEntry[] | undefined,
): JSX.Element | null {
  const missing = (coverage ?? []).filter((entry) => !entry.covered);

  if (missing.length === 0) {
    return null;
  }

  const title = missing.map((entry) => mapDietaryWarning(`MISSING_${entry.code}`)).join(', ');

  return (
    <span title={title} role="img" aria-label={title}>
      <AlertTriangle aria-hidden="true" className="size-4 text-amber-500" />
    </span>
  );
}

interface CalendarCellEntryProps {
  entry: MenuCalendarEntry;
  menuName: string;
  canManage: boolean;
  onEdit?: ((entry: MenuCalendarEntry) => void) | undefined;
  onTogglePublish?: ((entry: MenuCalendarEntry) => void) | undefined;
  onDelete?: ((entry: MenuCalendarEntry) => void) | undefined;
}

function CalendarCellEntry({
  entry,
  menuName,
  canManage,
  onEdit,
  onTogglePublish,
  onDelete,
}: CalendarCellEntryProps): JSX.Element {
  const publishLabel = entry.isPublished ? 'Despublicar' : 'Publicar';

  return (
    <div className="flex flex-col gap-1 rounded-lg border border-slate-100 bg-slate-50/60 p-1.5">
      <span className="line-clamp-2 text-sm font-medium text-slate-900">{menuName}</span>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">{formatQuota(entry)}</span>
        <StatusBadge domain="calendar" status={entry.isPublished ? 'PUBLISHED' : 'DRAFT'} />
      </div>
      {canManage ? (
        <div className="flex items-center gap-1 pt-1">
          <Button
            variant="ghost"
            size="sm"
            className="px-2"
            icon={Pencil}
            aria-label="Editar"
            title="Editar"
            onClick={() => onEdit?.(entry)}
          >
            <span className="sr-only">Editar</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="px-2"
            icon={entry.isPublished ? Undo2 : Send}
            aria-label={publishLabel}
            title={publishLabel}
            onClick={() => onTogglePublish?.(entry)}
          >
            <span className="sr-only">{publishLabel}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="px-2"
            icon={Trash2}
            aria-label="Eliminar"
            title="Eliminar"
            onClick={() => onDelete?.(entry)}
          >
            Eliminar
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** `reservedQuantity/maxQuantity`, or "Sin límite" (with the reserved count prefixed once there is at least one reservation) when `maxQuantity` is null. */
function formatQuota(entry: MenuCalendarEntry): string {
  if (entry.maxQuantity === null) {
    return entry.reservedQuantity > 0
      ? `${entry.reservedQuantity} reservados · sin límite`
      : 'Sin límite';
  }

  return `${entry.reservedQuantity}/${entry.maxQuantity}`;
}
